import { Platform } from 'react-native';

/**
 * Cross-tab coordinator: leader election + pub/sub message bus.
 *
 * Problem this solves
 * -------------------
 * Every open browser tab independently opens its own Socket.IO connection,
 * re-syncs the chat list on focus, and checks for service-worker updates on a
 * timer. The backend cost scales linearly with the number of open tabs even
 * though the work is largely redundant — only one tab needs to do it.
 *
 * Solution
 * --------
 * Elect a single "leader" tab using heartbeat-based election over
 * BroadcastChannel (with a localStorage `storage`-event fallback for older
 * Safari). The leader is the only tab permitted to perform shared background
 * work (SW update checks, etc.). Visibility-gated work (chat-list sync) is
 * additionally deduped across tabs via the message bus.
 *
 * The Socket.IO connection itself is NOT leader-only — it is paused when a
 * tab is hidden (see `lib/socket.js`), which is what bounds the number of
 * concurrent connections to the number of *visible* tabs (typically 1).
 *
 * Algorithm
 * ---------
 *  - Each tab generates a random tab ID at module load.
 *  - On start, a tab broadcasts an `election` message. Any existing leader
 *    re-asserts itself with a `leader` message.
 *  - If no leader responds within a randomized backoff window, the candidate
 *    claims leadership and starts broadcasting a `heartbeat` every 2s.
 *  - Followers track `lastLeaderHeartbeatAt`. If they don't observe a
 *    heartbeat for 5s (≈ 2 missed beats), they run a new election.
 *  - Split-brain (two leaders) is resolved deterministically by lowest tab
 *    ID: the higher-ID leader steps down.
 *  - The leader sends `stepdown` on `pagehide`/`beforeunload` so followers
 *    re-elect immediately rather than waiting out the staleness window.
 *
 * Native
 * ------
 * On iOS/Android there is exactly one window, so the coordinator is a no-op
 * that always reports `isLeader === true` and drops outbound messages.
 */

const CHANNEL_NAME = 'gab-ai-cross-tab';
const STORAGE_FALLBACK_KEY = 'gab-ai:xtab';
const HEARTBEAT_INTERVAL_MS = 2000;
const LEADER_STALE_MS = 5000; // ≈ 2 missed heartbeats before re-electing
const ELECTION_BACKOFF_MAX_MS = 300; // randomized candidacy window

const IS_WEB = Platform.OS === 'web';
const HAS_WINDOW = typeof window !== 'undefined';
const HAS_BROADCAST =
  HAS_WINDOW && typeof window.BroadcastChannel !== 'undefined';

function randomTabId() {
  // 12-char random ID. Lower IDs win split-brain, but since they're random
  // the identity of the leader is uniformly distributed across tabs.
  const rand = Math.random().toString(36).slice(2, 10);
  const time = Date.now().toString(36).slice(-4);
  return rand + time;
}

class CrossTabCoordinator {
  constructor() {
    this.tabId = randomTabId();
    this.isLeader = false;
    this.leaderId = null;
    this.lastLeaderHeartbeatAt = 0;

    this._started = false;
    this._channel = null;
    this._storageListener = null;
    this._unloadHandler = null;

    this._heartbeatTimer = null;
    this._electionTimer = null;
    this._watchdogTimer = null;

    this._leaderListeners = new Set();
    this._messageListeners = new Map(); // channel -> Set<cb>

    // Native / SSR: single window. Always the leader, no transport work.
    if (!IS_WEB || !HAS_WINDOW) {
      this.isLeader = true;
      this._started = true;
    }
  }

  /** Idempotent. Auto-invoked at module load on web; safe to call again. */
  start() {
    if (!IS_WEB || !HAS_WINDOW) return;
    if (this._started) return;
    this._started = true;

    if (HAS_BROADCAST) {
      this._channel = new BroadcastChannel(CHANNEL_NAME);
      this._channel.onmessage = (e) => this._handleMessage(e.data);
    } else if (typeof window !== 'undefined' && window.localStorage) {
      this._storageListener = (e) => {
        if (e.key !== STORAGE_FALLBACK_KEY || !e.newValue) return;
        try {
          this._handleMessage(JSON.parse(e.newValue));
        } catch (_) {
          // malformed payload from another tab — ignore
        }
      };
      window.addEventListener('storage', this._storageListener);
    }

    // Best-effort stepdown on unload so followers re-elect immediately
    // rather than waiting out the staleness window. Browsers may not always
    // deliver this (especially BroadcastChannel on bfcache restore), but the
    // heartbeat watchdog covers that case.
    this._unloadHandler = () => {
      if (this.isLeader) this._send({ type: 'stepdown', tabId: this.tabId });
    };
    window.addEventListener('pagehide', this._unloadHandler);
    window.addEventListener('beforeunload', this._unloadHandler);

    this._beginElection();
    // Watchdog: detect a stale/dead leader and trigger a new election.
    this._watchdogTimer = setInterval(
      () => this._beginElectionIfStale(),
      HEARTBEAT_INTERVAL_MS,
    );
  }

  _beginElectionIfStale() {
    if (this.isLeader) return;
    if (
      this.leaderId &&
      Date.now() - this.lastLeaderHeartbeatAt < LEADER_STALE_MS
    ) {
      return;
    }
    this._beginElection();
  }

  _beginElection() {
    if (!IS_WEB || !HAS_WINDOW) return;
    if (this.isLeader) return;
    if (this._electionTimer) return; // already running

    // Ask any existing leader to announce itself.
    this._send({ type: 'election', tabId: this.tabId });

    // Randomized backoff so two simultaneous candidates don't both seize
    // leadership at the same instant (split-brain is still resolved by ID
    // if they do, but this avoids the stepdown churn).
    const delay = 50 + Math.random() * ELECTION_BACKOFF_MAX_MS;
    this._electionTimer = setTimeout(() => {
      this._electionTimer = null;
      if (this.isLeader) return;
      if (
        !this.leaderId ||
        Date.now() - this.lastLeaderHeartbeatAt >= LEADER_STALE_MS
      ) {
        this._claimLeadership();
      }
    }, delay);
  }

  _claimLeadership() {
    this.isLeader = true;
    this.leaderId = this.tabId;
    this.lastLeaderHeartbeatAt = Date.now();
    this._send({ type: 'leader', tabId: this.tabId });
    this._startHeartbeat();
    this._notifyLeaderListeners(true);
  }

  _startHeartbeat() {
    if (this._heartbeatTimer) clearInterval(this._heartbeatTimer);
    this._heartbeatTimer = setInterval(() => {
      this._send({ type: 'heartbeat', tabId: this.tabId });
    }, HEARTBEAT_INTERVAL_MS);
  }

  _stepDown() {
    if (!this.isLeader) return;
    this.isLeader = false;
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
    this._send({ type: 'stepdown', tabId: this.tabId });
    this.leaderId = null;
    this.lastLeaderHeartbeatAt = 0;
    this._notifyLeaderListeners(false);
    this._beginElection();
  }

  _handleMessage(msg) {
    if (!msg || !msg.type) return;

    switch (msg.type) {
      case 'leader': {
        if (msg.tabId === this.tabId) return;
        if (this.isLeader) {
          // Split-brain: lowest tab ID wins.
          if (msg.tabId < this.tabId) {
            this._stepDown();
            this.leaderId = msg.tabId;
            this.lastLeaderHeartbeatAt = Date.now();
          } else {
            // We outrank the newcomer — re-assert so it steps down.
            this._send({ type: 'leader', tabId: this.tabId });
          }
        } else {
          this.leaderId = msg.tabId;
          this.lastLeaderHeartbeatAt = Date.now();
          // Cancel any pending election now that we know a leader is alive.
          if (this._electionTimer) {
            clearTimeout(this._electionTimer);
            this._electionTimer = null;
          }
        }
        break;
      }
      case 'heartbeat': {
        if (msg.tabId === this.leaderId) {
          this.lastLeaderHeartbeatAt = Date.now();
        }
        break;
      }
      case 'election': {
        // An existing leader re-asserts so the candidate doesn't grab
        // leadership out from under it.
        if (this.isLeader) {
          this._send({ type: 'leader', tabId: this.tabId });
        }
        break;
      }
      case 'stepdown': {
        if (msg.tabId === this.leaderId) {
          this.leaderId = null;
          this.lastLeaderHeartbeatAt = 0;
          this._beginElection();
        }
        break;
      }
      case 'data': {
        if (typeof msg.channel !== 'string') return;
        const cbs = this._messageListeners.get(msg.channel);
        if (cbs) {
          cbs.forEach((cb) => {
            try {
              cb(msg.payload);
            } catch (e) {
              console.error('[crossTab] message listener threw', e);
            }
          });
        }
        break;
      }
    }
  }

  _send(msg) {
    if (!IS_WEB || !HAS_WINDOW) return;
    const payload = { ...msg, ts: Date.now() };
    if (this._channel) {
      try {
        this._channel.postMessage(payload);
      } catch (_) {
        // transport closed — best effort
      }
    } else if (window.localStorage) {
      try {
        // `storage` events do not fire in the writing tab, only in others —
        // which is exactly what we want for cross-tab fan-out.
        window.localStorage.setItem(
          STORAGE_FALLBACK_KEY,
          JSON.stringify(payload),
        );
      } catch (_) {
        // quota / private mode — silently drop
      }
    }
  }

  _notifyLeaderListeners(isLeader) {
    this._leaderListeners.forEach((cb) => {
      try {
        cb(isLeader);
      } catch (e) {
        console.error('[crossTab] leader listener threw', e);
      }
    });
  }

  /**
   * Subscribe to leadership changes. The callback is invoked immediately with
   * the current state and again whenever this tab gains or loses leadership.
   * Returns an unsubscribe function.
   */
  onLeaderChange(cb) {
    if (typeof cb !== 'function') return () => {};
    this._leaderListeners.add(cb);
    try {
      cb(this.isLeader);
    } catch (e) {
      console.error('[crossTab] leader listener threw', e);
    }
    return () => this._leaderListeners.delete(cb);
  }

  /**
   * Subscribe to messages on a named channel. Returns an unsubscribe function.
   * Note: messages sent from this tab are NOT delivered to this tab's own
   * listeners — BroadcastChannel and `storage` events both have that semantic.
   */
  onMessage(channel, cb) {
    if (typeof channel !== 'string' || typeof cb !== 'function') {
      return () => {};
    }
    if (!this._messageListeners.has(channel)) {
      this._messageListeners.set(channel, new Set());
    }
    this._messageListeners.get(channel).add(cb);
    return () => {
      const set = this._messageListeners.get(channel);
      if (!set) return;
      set.delete(cb);
      if (set.size === 0) this._messageListeners.delete(channel);
    };
  }

  /** Broadcast a message to all *other* tabs on the named channel. */
  send(channel, payload) {
    if (typeof channel !== 'string') return;
    this._send({ type: 'data', channel, payload });
  }
}

const crossTab = new CrossTabCoordinator();

// Auto-start on web after the current synchronous import chain completes.
// Deferring one tick avoids any chance of BroadcastChannel being constructed
// during module evaluation before the JS environment is fully ready (rare,
// but observed in some bundling configurations).
if (IS_WEB && HAS_WINDOW) {
  setTimeout(() => {
    try {
      crossTab.start();
    } catch (e) {
      console.error('[crossTab] start failed', e);
    }
  }, 0);
}

export default crossTab;
export { CrossTabCoordinator };
